import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MapPin, Train, ShoppingBag, GraduationCap, Heart, Building2, Download, FileText } from 'lucide-react';
import { Logo } from '../components/Logo';
import LeadForm from '../components/LeadForm';
import LeadMagnetModal from '../components/LeadMagnetModal';
import Breadcrumbs from '../components/Breadcrumbs';

const META_TITLE = "The Ultimate 2026 Guide to Living and Investing in Rahatani | Best Residential Area in PCMC";
const META_DESCRIPTION = "Why Rahatani is the Golden Triangle of PCMC in 2026 — BRTS Road, operational Pune Metro, Phoenix Mall of the Millennium, top schools, and premium 2 & 3 BHK flats near DMart. Complete area guide for buyers and investors.";

const RahataniGuidePage = () => {
  const [leadMagnetOpen, setLeadMagnetOpen] = useState(false);
  const [leadMagnetVariant, setLeadMagnetVariant] = useState('connectivity-map');

  useEffect(() => {
    document.title = META_TITLE;

    // Update meta description for SPA
    const metaDescTag = document.querySelector('meta[name="description"]');
    const originalDesc = metaDescTag?.getAttribute('content');
    if (metaDescTag) metaDescTag.setAttribute('content', META_DESCRIPTION);

    // Article + FAQ JSON-LD schema
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "The Ultimate 2026 Guide to Living and Investing in Rahatani",
      "description": META_DESCRIPTION,
      "datePublished": "2026-04-29",
      "author": { "@type": "Organization", "name": "Nova Homes" },
      "publisher": { "@type": "Organization", "name": "Nova Homes", "url": "https://novahomes.in" },
      "image": "https://novahomes.in/images/silverwoods.webp",
      "mainEntityOfPage": "https://novahomes.in/rahatani-guide-2026"
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is Rahatani a good area to buy a flat in Pune?",
          "acceptedAnswer": { "@type": "Answer", "text": "Rahatani is widely considered one of the best residential areas in PCMC in 2026, with the operational Pune Metro Line 1 (Sant Tukaram Nagar station ~5 min away), Aundh-Ravet BRTS Road, DMart Thergaon at 800m, Phoenix Mall of the Millennium nearby, and 2 & 3 BHK flats starting from ₹70 Lakhs." }
        },
        {
          "@type": "Question",
          "name": "How far is Rahatani from Hinjewadi IT Park?",
          "acceptedAnswer": { "@type": "Answer", "text": "Rahatani is approximately 8 km from Hinjewadi Phase 1, with a typical drive time of 15 minutes via the BRTS Road — significantly faster than living inside Phase 1 during peak traffic." }
        },
        {
          "@type": "Question",
          "name": "What is the price of 2 BHK flats in Rahatani?",
          "acceptedAnswer": { "@type": "Answer", "text": "2 BHK flats in Rahatani in 2026 start around ₹70 Lakhs at projects like Sneha Avenue, with premium options like Silver Woods ranging from ₹72 Lakhs to ₹96 Lakhs depending on configuration and floor." }
        },
        {
          "@type": "Question",
          "name": "Which Pune Metro station is nearest to Rahatani?",
          "acceptedAnswer": { "@type": "Answer", "text": "Sant Tukaram Nagar station of the operational PCMC-Swargate Metro Line 1 is the closest station, approximately 5 minutes by road from Rahatani." }
        }
      ]
    };
    const a = document.createElement('script');
    a.type = 'application/ld+json';
    a.text = JSON.stringify(articleSchema);
    a.setAttribute('data-pillar', 'article');
    document.head.appendChild(a);
    const f = document.createElement('script');
    f.type = 'application/ld+json';
    f.text = JSON.stringify(faqSchema);
    f.setAttribute('data-pillar', 'faq');
    document.head.appendChild(f);
    return () => {
      document.head.removeChild(a);
      document.head.removeChild(f);
      if (metaDescTag && originalDesc) metaDescTag.setAttribute('content', originalDesc);
      document.title = 'Nova Homes Pune | 2 & 3 BHK Flats in Rahatani | Book Site Visit';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="rahatani-pillar-page">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-white/[0.04] sticky top-0 z-40">
        <div className="container-custom h-[72px] flex items-center justify-between">
          <Logo variant="light" />
          <div className="flex items-center gap-4">
            <Link
              to="/blog"
              data-testid="header-insights-link"
              className="hidden md:inline-block text-white/60 hover:text-white text-xs uppercase tracking-[0.2em] font-medium transition-colors"
            >
              Insights
            </Link>
            <Link
              to="/"
              data-testid="back-home-link"
              className="flex items-center gap-1.5 text-white/40 hover:text-white/70 text-sm font-light transition-colors"
            >
              <ArrowLeft size={15} />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
          </div>
        </div>
        <div className="container-custom py-4">
          <Breadcrumbs items={[
            { label: 'Home', href: '/' },
            { label: 'Rahatani Guide 2026' }
          ]} />
        </div>
      </header>

      {/* Hero Section */}
      <section data-testid="pillar-hero" className="relative bg-[#0a0a0a] text-white py-24 md:py-36 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src="/images/silverwoods.webp"
            alt="Rahatani Pune skyline — premium residential area in PCMC near Hinjewadi"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/30" />
        </div>
        <div className="container-custom relative">
          <p className="text-[#C5A46D] text-xs uppercase tracking-[0.3em] font-medium mb-6">2026 Pillar Guide</p>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] mb-8 max-w-5xl">
            The Ultimate 2026 Guide to Living and Investing in Rahatani
          </h1>
          <p className="text-white/70 text-lg md:text-xl font-light leading-relaxed max-w-3xl mb-10">
            Why Rahatani has quietly become the <span className="text-[#C5A46D] font-medium">Golden Triangle of PCMC</span> — sitting at the intersection of the BRTS corridor, the operational Pune Metro, and the Hinjewadi IT belt — offering working professionals and families a rare combination of connectivity, lifestyle, and value that no other Pune locality matches in this price band.
          </p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-white/50 uppercase tracking-[0.2em]">
            <span>10 min read</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>Last updated · April 2026</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>By Nova Homes Editorial</span>
          </div>
        </div>
      </section>

      {/* Why Golden Triangle */}
      <section data-testid="golden-triangle-section" className="py-24 md:py-32 bg-white">
        <div className="container-custom max-w-4xl">
          <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Introduction</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111] leading-tight mb-8">
            Why Rahatani is the Golden Triangle of PCMC
          </h2>
          <div className="space-y-6 text-[#444] text-base md:text-lg font-light leading-[1.85]">
            <p>
              For decades, PCMC's spotlight bounced between Wakad, Pimple Saudagar, and Aundh. In 2026, a quieter, smarter pocket has emerged as the most balanced address in Pune's western corridor — <strong className="font-semibold text-[#111]">Rahatani</strong>. Sitting at the geometric intersection of the Aundh-Ravet BRTS Road, the operational PCMC-Swargate Metro line, and the Hinjewadi IT belt, Rahatani is the rare locality that doesn't force you to choose between commute, lifestyle, or budget.
            </p>
            <p>
              We call it the Golden Triangle of PCMC because three forces meet here: <strong className="font-semibold text-[#111]">infrastructure that's already built and operational</strong>, <strong className="font-semibold text-[#111]">lifestyle anchors that are open today</strong> (DMart, Phoenix Mall, top schools), and <strong className="font-semibold text-[#111]">pricing that hasn't yet caught up</strong> with the value being delivered. Whether you're a first-home buyer, an upgrader, or an investor, the next 24 months in Rahatani are arguably the most asymmetric opportunity in Pune real estate.
            </p>
          </div>
        </div>
      </section>

      {/* Infrastructure Section */}
      <section data-testid="infrastructure-section" className="py-24 md:py-32 bg-[#FAFAF8]">
        <div className="container-custom max-w-5xl">
          <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Connectivity & Infrastructure</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111] leading-tight mb-12">
            The Three Engines Powering Rahatani's Growth
          </h2>

          <div className="space-y-12">
            {/* BRTS */}
            <div className="grid md:grid-cols-[80px_1fr] gap-6 md:gap-10">
              <div className="flex md:justify-center">
                <div className="w-16 h-16 bg-[#111] text-[#C5A46D] flex items-center justify-center rounded-sm">
                  <MapPin size={26} />
                </div>
              </div>
              <div>
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-[#111] mb-3">
                  Aundh-Ravet BRTS Road — The Spine of the Western Corridor
                </h3>
                <p className="text-[#444] text-base font-light leading-[1.85] mb-4">
                  The Aundh-Ravet Bus Rapid Transit System is more than a bus lane — it's the only dedicated, signal-managed corridor in PCMC, slashing commute times by 30-40% versus regular roads. Living on or beside the BRTS means you reach Hinjewadi Phase 1 in 15 minutes, Aundh in 12 minutes, and Pimpri in 8 minutes — without fighting general traffic.
                </p>
                <ul className="space-y-2 text-[#444] text-sm font-light">
                  <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />Direct BRTS access from Rahatani — no interchange to reach Hinjewadi</li>
                  <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />Signal-managed flow to Aundh, Pimpri, Wakad, Hinjewadi</li>
                  <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />Integrated with DP Road, Mumbai-Bangalore Highway, and the inner-city grid</li>
                </ul>
              </div>
            </div>

            {/* Metro */}
            <div className="grid md:grid-cols-[80px_1fr] gap-6 md:gap-10">
              <div className="flex md:justify-center">
                <div className="w-16 h-16 bg-[#111] text-[#C5A46D] flex items-center justify-center rounded-sm">
                  <Train size={26} />
                </div>
              </div>
              <div>
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-[#111] mb-3">
                  Operational Pune Metro — Sant Tukaram Nagar Just 5 Minutes Away
                </h3>
                <p className="text-[#444] text-base font-light leading-[1.85] mb-4">
                  The PCMC-Swargate Metro (Line 1, Purple Line) has been operational since September 2024, and Sant Tukaram Nagar — the closest station to Rahatani — is approximately 5 minutes by road. From there, you reach Pimpri, Shivaji Nagar, Civil Court, and Swargate without driving through any traffic snarl. This isn't an "upcoming" advantage — it's already running.
                </p>
                <ul className="space-y-2 text-[#444] text-sm font-light">
                  <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />Operational since Sep 2024 — daily 6 AM to 11 PM service</li>
                  <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />Direct connection: Pimpri → Sant Tukaram Nagar → Civil Court → Swargate</li>
                  <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />Future Hinjewadi-Civil Court line (Line 3) under construction — additional access point</li>
                </ul>
              </div>
            </div>

            {/* Phoenix Mall */}
            <div className="grid md:grid-cols-[80px_1fr] gap-6 md:gap-10">
              <div className="flex md:justify-center">
                <div className="w-16 h-16 bg-[#111] text-[#C5A46D] flex items-center justify-center rounded-sm">
                  <ShoppingBag size={26} />
                </div>
              </div>
              <div>
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-[#111] mb-3">
                  Phoenix Mall of the Millennium — A Lifestyle Anchor at Your Doorstep
                </h3>
                <p className="text-[#444] text-base font-light leading-[1.85] mb-4">
                  Pune's largest premium retail destination — Phoenix Mall of the Millennium in Wakad — is approximately 10 minutes from Rahatani. Beyond shopping, the mall has become a social hub for PCMC: weekend dining, premium cinema, kids' play zones, and brand experiences that previously required a trip to Koregaon Park or Phoenix Marketcity. Property values within 5 km of Phoenix have appreciated 18-22% since the mall opened.
                </p>
                <ul className="space-y-2 text-[#444] text-sm font-light">
                  <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />200+ premium brands, 14-screen multiplex, 40+ F&B outlets</li>
                  <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />Documented price uplift effect on residential pockets within 5 km</li>
                  <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] flex-shrink-0 mt-2" />Weekend retail without the Mumbai-Bangalore highway crawl</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mid-page Brochure CTA banner */}
      <section data-testid="pillar-brochure-banner" className="py-12 md:py-16 bg-white border-y border-[#111]/[0.06]">
        <div className="container-custom max-w-5xl">
          <div className="relative bg-gradient-to-br from-[#0a0a0a] via-[#14120c] to-[#0a0a0a] overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-1 bg-[#C5A46D]" />
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#C5A46D]/10 blur-3xl pointer-events-none" />
            <div className="relative px-7 py-10 md:px-10 md:py-12 flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-14 h-14 bg-[#C5A46D]/15 text-[#C5A46D] flex items-center justify-center flex-shrink-0 rounded-sm">
                <FileText size={24} />
              </div>
              <div className="flex-1">
                <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.3em] font-medium mb-2">Free PDF · 3 Projects · Instant</p>
                <h2 className="font-heading text-2xl md:text-3xl text-white font-semibold leading-tight mb-2">
                  Get Floor Plans &amp; Price Sheet
                </h2>
                <p className="text-white/55 text-sm font-light leading-relaxed max-w-2xl">
                  Configurations, carpet areas, indicative pricing, amenities, and RERA for all three Nova Homes projects — one branded PDF, delivered instantly.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setLeadMagnetVariant('brochure'); setLeadMagnetOpen(true); }}
                data-testid="pillar-brochure-btn"
                className="group inline-flex items-center justify-center gap-2 bg-[#C5A46D] text-[#0a0a0a] py-3.5 px-6 font-semibold text-sm tracking-wide hover:bg-[#D4AF37] transition-colors flex-shrink-0"
              >
                <Download size={16} className="group-hover:translate-y-[1px] transition-transform" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Lifestyle Grid */}
      <section data-testid="lifestyle-section" className="py-24 md:py-32 bg-white">
        <div className="container-custom max-w-5xl">
          <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Daily Life & Lifestyle</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111] leading-tight mb-4">
            Everything You Need, Within 10 Minutes
          </h2>
          <p className="text-[#666] text-base font-light leading-relaxed mb-12 max-w-3xl">
            Rahatani isn't a satellite suburb you commute out of — it's a complete neighbourhood with schools, hospitals, retail, and dining all within a short drive.
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { icon: ShoppingBag, title: "DMart Thergaon", subtitle: "800m · 2 min drive", desc: "Daily essentials, groceries, household — at the doorstep" },
              { icon: GraduationCap, title: "SNBP International School", subtitle: "5 min drive", desc: "ICSE curriculum, well-regarded for academic outcomes" },
              { icon: Heart, title: "Lotus Hospital", subtitle: "8 min drive", desc: "Multi-speciality care nearby for emergencies and routine visits" },
              { icon: Building2, title: "Phoenix Mall of the Millennium, Wakad", subtitle: "10 min drive", desc: "Premium retail, dining, multiplex — weekends sorted" },
              { icon: GraduationCap, title: "Wisdom World School", subtitle: "5 min drive", desc: "CBSE-affiliated, large campus, sports infrastructure" },
              { icon: Heart, title: "Aditya Birla Memorial Hospital", subtitle: "10 min drive", desc: "Tertiary care hospital, specialist consultations" },
              { icon: Train, title: "Sant Tukaram Nagar Metro Station", subtitle: "5 min drive", desc: "Operational PCMC-Swargate Metro Line 1" },
              { icon: MapPin, title: "Hinjewadi IT Park", subtitle: "8 km · 15 min drive", desc: "Direct BRTS commute — no traffic interchange" },
              { icon: MapPin, title: "Pimpri Railway Station", subtitle: "5 min drive", desc: "Outstation rail connectivity for weekend travel" }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} data-testid={`lifestyle-card-${i}`} className="border border-[#111]/[0.08] p-6 hover:border-[#C5A46D]/40 transition-colors">
                  <Icon size={20} className="text-[#C5A46D] mb-4" />
                  <h3 className="font-heading text-base font-semibold text-[#111] mb-1">{item.title}</h3>
                  <p className="text-[#C5A46D] text-xs uppercase tracking-[0.15em] font-medium mb-3">{item.subtitle}</p>
                  <p className="text-[#666] text-sm font-light leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lead-magnet CTA band */}
      <section data-testid="pillar-leadmagnet-band" className="py-16 md:py-20 bg-[#0a0a0a] text-white border-t border-white/[0.04]">
        <div className="container-custom max-w-4xl">
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="w-14 h-14 bg-[#C5A46D]/15 text-[#C5A46D] flex items-center justify-center flex-shrink-0 rounded-sm">
              <FileText size={22} />
            </div>
            <div className="flex-1">
              <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.3em] font-medium mb-2">Free PDF · 1-Page Reference</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-2 leading-tight">
                Get the Rahatani Connectivity Map
              </h2>
              <p className="text-white/50 text-sm font-light leading-relaxed max-w-2xl">
                Every landmark you'll actually use — DMart, BRTS, Sant Tukaram Nagar Metro, Hinjewadi, schools, hospitals — with exact distances and drive-times. Field-verified, instantly downloadable.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setLeadMagnetVariant('connectivity-map'); setLeadMagnetOpen(true); }}
              data-testid="pillar-download-map-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#C5A46D] text-[#0a0a0a] py-3.5 px-6 font-medium text-sm tracking-wide hover:bg-[#D4AF37] transition-colors flex-shrink-0"
            >
              <Download size={16} />
              Download PDF
            </button>
          </div>
        </div>
      </section>

      {/* Premium Living Options */}
      <section data-testid="premium-living-section" className="py-24 md:py-32 bg-[#0a0a0a] text-white">
        <div className="container-custom max-w-5xl">
          <p className="text-[#C5A46D] text-xs uppercase tracking-[0.3em] font-medium mb-4">Premium Living Options</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold leading-tight mb-4">
            Where to Live in Rahatani — Curated by Nova Homes
          </h2>
          <p className="text-white/60 text-base font-light leading-relaxed mb-12 max-w-3xl">
            Two of our most sought-after projects sit at the heart of Rahatani's Golden Triangle. Each is RERA-registered, deeply researched, and priced to reward early buyers.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Silver Woods card */}
            <Link
              to="/project/silver-woods"
              data-testid="pillar-link-silver-woods"
              className="group bg-white/[0.03] border border-white/[0.08] hover:border-[#C5A46D]/40 transition-all duration-500 overflow-hidden"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src="/images/silverwoods.webp"
                  alt="Silver Woods — premium 2 & 3 BHK apartments in Rahatani Pune near DMart and BRTS Road"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
              </div>
              <div className="p-8">
                <p className="text-[#C5A46D] text-xs uppercase tracking-[0.2em] font-medium mb-3">2 & 3 BHK Apartments</p>
                <h3 className="font-heading text-2xl font-semibold mb-3">Silver Woods</h3>
                <p className="text-white/60 text-sm font-light leading-relaxed mb-5">
                  Spacious 763 sq ft (2 BHK) and 960 sq ft (3 BHK) carpet areas with rooftop open gym, yoga deck and meditation space. RERA: P52100054401.
                </p>
                <div className="flex items-center justify-between pt-5 border-t border-white/[0.06]">
                  <span className="text-white/80 text-base font-medium">₹72L – ₹96L</span>
                  <span className="inline-flex items-center gap-2 text-[#C5A46D] text-sm font-medium group-hover:gap-3 transition-all">
                    View Project <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>

            {/* Aurelia card */}
            <Link
              to="/project/aurelia"
              data-testid="pillar-link-aurelia"
              className="group bg-white/[0.03] border border-white/[0.08] hover:border-[#C5A46D]/40 transition-all duration-500 overflow-hidden"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src="/images/aurelia.webp"
                  alt="Aurelia — premium 3 BHK apartments in Shivraj Nagar Rahatani Pune near BRTS Road and Phoenix Mall"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
              </div>
              <div className="p-8">
                <p className="text-[#C5A46D] text-xs uppercase tracking-[0.2em] font-medium mb-3">Premium 3 BHK</p>
                <h3 className="font-heading text-2xl font-semibold mb-3">Aurelia</h3>
                <p className="text-white/60 text-sm font-light leading-relaxed mb-5">
                  Low-density 7-storey development with only 28 exclusive 3 BHK units, 1009 sq ft carpet, and rooftop cafeteria deck. RERA: P52100077244.
                </p>
                <div className="flex items-center justify-between pt-5 border-t border-white/[0.06]">
                  <span className="text-white/80 text-base font-medium">Starting ₹99.50L</span>
                  <span className="inline-flex items-center gap-2 text-[#C5A46D] text-sm font-medium group-hover:gap-3 transition-all">
                    View Project <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Lead form */}
      <section data-testid="pillar-cta-section" className="py-24 md:py-32 bg-[#FAFAF8]">
        <div className="container-custom max-w-2xl">
          <div className="text-center mb-10">
            <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Ready to Visit Rahatani?</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111] leading-tight mb-4">
              Schedule a Site Visit
            </h2>
            <p className="text-[#666] text-base font-light">
              Walk through Silver Woods or Aurelia, see the BRTS proximity firsthand, and get every question answered.
            </p>
          </div>
          <LeadForm projectName="Rahatani Guide" source="rahatani-pillar-page" />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white py-16 border-t border-white/[0.04]">
        <div className="container-custom text-center">
          <Logo variant="light" className="justify-center mb-6" />
          <p className="text-white/40 text-sm font-light mb-2">Nova Homes · Pune, Maharashtra</p>
          <p className="text-white/30 text-xs font-light">© 2026 Nova Homes. All rights reserved.</p>
        </div>
      </footer>

      <LeadMagnetModal open={leadMagnetOpen} onClose={() => setLeadMagnetOpen(false)} source="rahatani-pillar-page" variant={leadMagnetVariant} />
    </div>
  );
};

export default RahataniGuidePage;
